<template>
  <div class="vx-col w-full mb-base flex flex-wrap">
    <div class="w-full lg:w-1/4 px-2 mb-4 lg:mb-0">
      <vx-card>
        <h4 class="mb-4">กรองข้อมูลโดย</h4>

        <div class="mb-4">
          <label class="block mb-2">วันที่คำนวณ</label>
          <vs-select v-model="filters.processDate" class="w-full">
            <vs-select-item value="" text="ทั้งหมด" />
            <vs-select-item
              v-for="date in processDateOptions"
              :key="date"
              :value="date"
              :text="formatDate(date)"
            />
          </vs-select>
        </div>

        <div class="overall-section mb-4">
          <label class="block mb-2 font-semibold">ผลการตรวจสอบรวม</label>
          <div class="radio-group">
            <vs-radio :vs-name="'overallFilter'" v-model="filters.overall" vs-value="all">ทั้งหมด</vs-radio>
            <vs-radio :vs-name="'overallFilter'" v-model="filters.overall" vs-value="pass">ผ่าน</vs-radio>
            <vs-radio :vs-name="'overallFilter'" v-model="filters.overall" vs-value="fail">ไม่ผ่าน</vs-radio>
          </div>
        </div>

        <div class="mt-4">
          <vs-button color="warning" type="border" class="w-full" @click="clearFilters">
            Clear Filter ทั้งหมด
          </vs-button>
        </div>
      </vx-card>
    </div>

    <div class="w-full lg:w-3/4 px-2">
      <vx-card>
        <div class="mb-4 record-counter">
          <div class="record-counter__title">จำนวนรายการที่แสดง</div>
          <div class="record-counter__value">{{ filteredContracts.length }}</div>
          <div class="record-counter__meta">จากทั้งหมด {{ normalizedContracts.length }} รายการ</div>
        </div>

        <vs-table :data="filteredContracts">
          <template #thead>
            <vs-th>ลำดับ</vs-th>
            <vs-th>ไอดีสัญญา</vs-th>
            <vs-th>{{ $t('sections.accounting.dataForm.contractNo') }}</vs-th>
            <vs-th>{{ $t('sections.accounting.dataForm.contractName') }}</vs-th>
            <vs-th>วันที่คำนวณ</vs-th>
            <vs-th>ผลการตรวจสอบรวม</vs-th>
            <vs-th>รายการ CalculationLogDetail</vs-th>
          </template>

          <template #default="{ data }">
            <template v-for="(row, index) in data">
              <vs-tr
                :key="row.id || index"
                :data="row"
              >
                <vs-td>
                  {{ index + 1 }}
                </vs-td>
                <vs-td>
                  {{ row.id }}
                </vs-td>
                <vs-td>
                  {{ row.contractNo }}
                </vs-td>
                <vs-td>
                  {{ row.contractName }}
                </vs-td>
                <vs-td>{{ row.processDate  }}</vs-td>
                <vs-td>
                  <span :class="booleanDisplayClass(row.isPassed)">{{ booleanDisplayText(row.isPassed) }}</span>
                </vs-td>
                <vs-td>
                  <div class="log-detail-cell">
                    <vs-button
                      v-if="row.calculationLogDetails.length"
                      size="small"
                      color="primary"
                      type="border"
                      @click="toggleDetail(row.id)"
                    >
                      {{ expandedContractIds[row.id] ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด' }} ({{ row.calculationLogDetails.length }})
                    </vs-button>
                    <span v-else class="text-muted">ไม่มีข้อมูล</span>
                  </div>
                </vs-td>
              </vs-tr>

              <vs-tr
                v-if="expandedContractIds[row.id]"
                :key="`${row.id || index}-details`"
                class="detail-row"
              >
                <vs-td colspan="7">
                  <div class="detail-card">
                    <div class="detail-card__title">รายละเอียด CalculationLogDetail</div>

                    <div class="detail-table-wrapper">
                      <table class="detail-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Model</th>
                            <th>Action</th>
                            <th>Payload</th>
                            <th>Payload Data</th>
                            <th>Payload Before Data</th>
                            <th>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(detail, detailIndex) in row.calculationLogDetails" :key="detail.id || detailIndex">
                            <td>{{ detailIndex + 1 }}</td>
                            <td>{{ detail.model || '-' }}</td>
                            <td>{{ detail.action || '-' }}</td>
                            <td>
                              <pre class="payload-block">{{ formatPayload(detail.payload) }}</pre>
                            </td>
                            <td>
                              <pre class="payload-block">{{ formatPayload(detail.payloadData) }}</pre>
                            </td>
                            <td>
                              <pre class="payload-block">{{ formatPayload(detail.payloadBeforeData) }}</pre>
                            </td>
                            <td>{{ formatDateTime(detail.createdAt) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </vs-td>
              </vs-tr>
            </template>
          </template>
        </vs-table>
      </vx-card>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const {
  mapActions: mapAccountingActions,
  mapState: mapAccountingState
} = createNamespacedHelpers('Accounting')

export default {
  name: 'ContractProcessLocked',
  data () {
    return {
      filters: {
        processDate: '',
        overall: 'all'
      },
      expandedContractIds: {}
    }
  },
  async created () {
    await this.getContractsProcessLocked()
  },
  computed: {
    ...mapAccountingState(['contracts', 'status']),
    normalizedContracts () {
      return this.contracts.map((contract) => this.normalizeContract(contract))
    },
    processDateOptions () {
      const uniqueDate = new Set(
        this.normalizedContracts
          .map((contract) => this.getProcessDate(contract))
          .filter(Boolean)
      )

      return [...uniqueDate].sort((current, next) => next.localeCompare(current))
    },
    filteredContracts () {
      return this.normalizedContracts.filter((contract) => {
        if (this.filters.processDate) {
          const contractDate = this.getProcessDate(contract)
          if (contractDate !== this.filters.processDate) {
            return false
          }
        }

        if (this.filters.overall === 'all') {
          return true
        }

        const isPassed = this.toBoolean(contract.isPassed)
        if (this.filters.overall === 'pass') {
          return isPassed
        }

        if (this.filters.overall === 'fail') {
          return !isPassed
        }

        return true
      })
    }
  },
  methods: {
    ...mapAccountingActions(['getContractsProcessLocked']),
    clearFilters () {
      this.filters = {
        processDate: '',
        overall: 'all'
      }
      this.expandedContractIds = {}
    },
    formatDate (value) {
      if (!value) {
        return '-'
      }

      const [year, month, day] = value.split('-')
      if (!year || !month || !day) {
        return value
      }

      return `${day}/${month}/${year}`
    },
    normalizeContract (contract) {
      const calculationLogDetails = ((contract.loan || {}).calculationLog || {}).calculationLogDetails || []

      return {
        ...contract,
        processDate: this.getProcessDate(contract),
        calculationLogDetails,
        isPassed: calculationLogDetails.length === 0
      }
    },
    toggleDetail (contractId) {
      this.$set(this.expandedContractIds, contractId, !this.expandedContractIds[contractId])
    },
    formatPayload (payload) {
      if (payload === null || payload === undefined || payload === '') {
        return '-'
      }

      const payloadObject = this.parsePayload(payload)

      if (payloadObject === null || payloadObject === undefined) {
        return String(payload)
      }

      if (typeof payloadObject === 'object') {
        return JSON.stringify(payloadObject, null, 2)
      }

      return String(payloadObject)
    },
    formatDateTime (value) {
      if (!value) {
        return '-'
      }

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return value
      }

      return `${date.toLocaleDateString('th-TH')} ${date.toLocaleTimeString('th-TH')}`
    },
    parsePayload (payload) {
      if (typeof payload === 'object') {
        return payload
      }

      if (typeof payload !== 'string') {
        return null
      }

      const directParsed = this.tryParseJson(payload)
      if (directParsed !== null) {
        return directParsed
      }

      const trimmedPayload = payload.trim()
      const unwrappedPayload = trimmedPayload.startsWith('"') && trimmedPayload.endsWith('"')
        ? trimmedPayload.slice(1, -1).replace(/""/g, '"')
        : trimmedPayload

      return this.tryParseJson(unwrappedPayload)
    },
    tryParseJson (value) {
      try {
        return JSON.parse(value)
      } catch (e) {
        return null
      }
    },
    getProcessDate (contract) {
      const processDate = (contract && ((contract.loan && contract.loan.processDate) || contract.processDate)) || ''
      return String(processDate).slice(0, 10)
    },
    toBoolean (value) {
      if (typeof value === 'boolean') {
        return value
      }

      if (typeof value === 'number') {
        return value === 1
      }

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return normalized === 'true' || normalized === '1' || normalized === 'yes'
      }

      return false
    },
    booleanDisplayText (value) {
      return this.toBoolean(value) ? 'ผ่าน' : 'ไม่ผ่าน'
    },
    booleanDisplayClass (value) {
      return this.toBoolean(value) ? 'status-pass' : 'status-fail'
    }
  },
  watch: {
    status: {
      handler (res) {
        if (res.processing !== null) {
          if (res.processing) {
            this.$vs.loading()
          } else {
            this.$vs.loading.close()
          }
        }

        if (res.success !== null) {
          this.$vs.notify({ ...res.success })
        } else if (res.error !== null) {
          this.$vs.notify({ ...res.error })
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.overall-section {
  padding-top: 10px;
  border-top: none;
}

.record-counter {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background-color: #f9fafb;
}

.record-counter__title {
  font-size: 12px;
  color: #6b7280;
}

.record-counter__value {
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  color: #1f2937;
}

.record-counter__meta {
  font-size: 12px;
  color: #6b7280;
}

.status-pass,
.status-fail {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-pass {
  background-color: #dcfce7;
  color: #166534;
}

.status-fail {
  background-color: #fee2e2;
  color: #b91c1c;
}

.log-detail-cell {
  min-width: 180px;
}

.detail-row td {
  background-color: #f8fafc;
}

.detail-card {
  border: 1px solid #dbeafe;
  background: #eff6ff;
  border-radius: 8px;
  padding: 10px;
}

.detail-card__title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #1e3a8a;
}

.detail-table-wrapper {
  overflow-x: auto;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
}

.detail-table th,
.detail-table td {
  border: 1px solid #dbeafe;
  text-align: left;
  padding: 8px;
  vertical-align: top;
  font-size: 12px;
}

.detail-table th {
  background-color: #dbeafe;
}

.payload-block {
  margin: 0;
  background: #111827;
  color: #f9fafb;
  border-radius: 6px;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-word;
  min-width: 260px;
  max-width: 420px;
  max-height: 220px;
  overflow: auto;
}
</style>
